import _isEqual from "lodash/isEqual";
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useDebounce } from 'use-debounce';
import { useTheme } from 'styled-components';
import { Box, Button, Flex, SegmentedControl, Select, Text } from 'core/components';
import { formatMetricValue } from 'app/views/ude/dataviews/util/unitFormatter';
import { buildMetricCharts } from './buildMetricQueries';
import CollapsibleChart from './CollapsibleChart';
import SynthFilterBar from '../../filters/SynthFilterBar';
import { TAB_FILTERS } from '../../filters/tabFilterConfig';
const CHART_OVERRIDES = {
  yAxis: {
    title: {
      text: ''
    }
  },
  plotOptions: {
    series: {
      // Perf: don't render a marker per data point (big win at high point counts);
      // keep a marker on hover so the tooltip still highlights the point.
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: true
          }
        }
      },
      // Allow dense series without Highcharts' turbo point-count cap (matches other synth charts).
      turboThreshold: 0
    }
  },
  // Keep the tooltip inside the chart SVG. The base UDE config uses `outside: true`, which
  // renders the shared tooltip in a document.body div; near the viewport edge it toggles a
  // page scrollbar and reflows every full-width chart ("jumpy" on hover).
  tooltip: {
    outside: false
  }
};
const CHART_SIZE_OPTIONS = [{
  label: 'Normal',
  value: 'normal'
}, {
  label: 'Compact',
  value: 'compact'
}];

// Fixed legend presentation: right-aligned, table-style legend with rollups.
const CHART_SETTINGS = {
  chart: {
    legend: {
      position: 'right',
      mode: 'table',
      showRollups: true
    }
  }
};

// Maps metric names to their healthSettings key prefix
const METRIC_TO_HEALTH_KEY = {
  ping_latency_avg: 'latency',
  ping_jitter_avg: 'jitter',
  ping_packet_loss_pct: 'packetLoss',
  dns_latency_avg: 'dnsLatency',
  http_latency_avg: 'httpLatency',
  pageload_latency_avg: 'httpLatency',
  http_status_code: 'httpStatusCode'
};

// Measurement unit per metric, so threshold labels render in the same unit as the chart axis
// (latency/jitter thresholds are stored in microseconds; packet loss is a percent).
const METRIC_TO_UNIT = {
  ping_latency_avg: 'duration:microseconds',
  ping_jitter_avg: 'duration:microseconds',
  ping_packet_loss_pct: 'percent',
  dns_latency_avg: 'duration:microseconds',
  http_latency_avg: 'duration:microseconds',
  pageload_latency_avg: 'duration:microseconds'
};
function getHealthPlotLines(test, metricName, colors) {
  const healthKey = METRIC_TO_HEALTH_KEY[metricName];
  if (!healthKey) {
    return [];
  }
  const unit = METRIC_TO_UNIT[metricName];
  const healthSettings = test.get('config.healthSettings') || {};
  const warning = healthSettings[`${healthKey}Warning`];
  const critical = healthSettings[`${healthKey}Critical`];
  const lines = [];
  if (warning && warning > 0) {
    lines.push({
      value: warning,
      color: colors.warning,
      dashStyle: 'Dash',
      width: 1,
      zIndex: 3,
      label: {
        text: `Warning: ${formatMetricValue(warning, unit).displayFull}`,
        style: {
          color: colors.warning,
          fontSize: '10px'
        }
      }
    });
  }
  if (critical && critical > 0) {
    lines.push({
      value: critical,
      color: colors.danger,
      dashStyle: 'Dash',
      width: 1,
      zIndex: 3,
      label: {
        text: `Critical: ${formatMetricValue(critical, unit).displayFull}`,
        style: {
          color: colors.danger,
          fontSize: '10px'
        }
      }
    });
  }
  return lines;
}
const VIZ_LIMIT_OPTIONS = [{
  label: '5',
  value: 5
}, {
  label: '10',
  value: 10
}, {
  label: '15',
  value: 15
}, {
  label: '20',
  value: 20
}, {
  label: '25',
  value: 25
}, {
  label: '30',
  value: 30
}, {
  label: '40',
  value: 40
}];
const SynthUdeMetricsPanel = observer(({
  test
}) => {
  const {
    results
  } = test;
  const {
    colors
  } = useTheme();
  const [vizLimit, setVizLimit] = useState(10);
  const [chartSize, setChartSize] = useState('normal');
  const chartHeight = chartSize === 'compact' ? 275 : 400;

  // Debounce filter values so rapid multi-select clicks fire a single query (~1s after last change).
  // Compare by content (isEqual): the shared-filter getters return a fresh array each render, which
  // would otherwise defeat use-debounce's change detection and cause a self-retriggering rebuild loop.
  const [debouncedAgentIds] = useDebounce(results.sharedFilterAgentIds.slice(), 1000, {
    equalityFn: _isEqual
  });
  const [debouncedTargets] = useDebounce(results.sharedFilterTargets.slice(), 1000, {
    equalityFn: _isEqual
  });
  const [debouncedLabelIds] = useDebounce(results.sharedFilterLabelIds.slice(), 1000, {
    equalityFn: _isEqual
  });
  const [debouncedSiteIds] = useDebounce(results.sharedFilterSiteIds.slice(), 1000, {
    equalityFn: _isEqual
  });
  const [debouncedAsns] = useDebounce(results.sharedFilterAsns.slice(), 1000, {
    equalityFn: _isEqual
  });
  const hasFilters = results.filters.isActive;
  const handleClearFilters = () => results.resetSharedFilters();
  const charts = useMemo(() => buildMetricCharts(test, {
    start: results.actualStartDate,
    end: results.actualEndDate
  }, {
    agentIds: debouncedAgentIds,
    targets: debouncedTargets,
    labelIds: debouncedLabelIds,
    siteIds: debouncedSiteIds,
    asns: debouncedAsns
  }, vizLimit), [test, results.actualStartDate, results.actualEndDate, debouncedAgentIds, debouncedTargets, debouncedLabelIds, debouncedSiteIds, debouncedAsns, vizLimit]);
  return /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    gap: 3,
    flex: 1,
    p: 2
  }, /*#__PURE__*/React.createElement(SynthFilterBar, {
    test: test,
    filters: TAB_FILTERS.metrics,
    title: "Filter Metrics",
    trailingControls: /*#__PURE__*/React.createElement(Flex, {
      ml: "auto",
      alignItems: "flex-start",
      gap: "12px"
    }, hasFilters && /*#__PURE__*/React.createElement(Button, {
      small: true,
      minimal: true,
      intent: "warning",
      onClick: handleClearFilters
    }, "Clear Filters"), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
      as: "div",
      small: true,
      muted: true,
      mb: "4px",
      fontWeight: "bold"
    }, "Series Depth"), /*#__PURE__*/React.createElement(Select, {
      menuWidth: 80,
      options: VIZ_LIMIT_OPTIONS,
      values: vizLimit,
      onChange: setVizLimit
    })), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
      as: "div",
      small: true,
      muted: true,
      mb: "4px",
      fontWeight: "bold"
    }, "Chart Size"), /*#__PURE__*/React.createElement(SegmentedControl, {
      options: CHART_SIZE_OPTIONS,
      value: chartSize,
      onChange: setChartSize
    })))
  }), charts.map(({
    key,
    title,
    query
  }) => {
    const metricName = query.metrics[0]?.name || '';
    const plotLines = getHealthPlotLines(test, metricName, colors);
    const overrides = plotLines.length > 0 ? {
      ...CHART_OVERRIDES,
      yAxis: {
        ...CHART_OVERRIDES.yAxis,
        plotLines
      }
    } : CHART_OVERRIDES;
    return /*#__PURE__*/React.createElement(CollapsibleChart, {
      key: key,
      title: title,
      query: query,
      chartHeight: chartHeight,
      chartOverrides: overrides,
      settings: CHART_SETTINGS
    });
  }));
});
export default SynthUdeMetricsPanel;